import * as XLSX from 'xlsx';
import { Patient, Prescription } from 'common/models/types';
import { meftDivName } from 'common/constants/meftDivCategories';

interface ExportMedicationHistoryParams {
  patient: Patient;
  filename?: string;
}

/**
 * 날짜를 YYYYMMDD 형식으로 변환 (내가먹는약한눈에 형식)
 */
const formatDateYYYYMMDD = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

/**
 * 880 표준코드에서 보험코드(9자리) 추출
 * 880 표준코드 구조: 880 + [보험코드 9자리] + [체크섬 1자리] = 13자리
 * 예: 8806646006901 → 664600690 추출
 */
const extractInsuranceCodeFromStandardCode = (standardCode?: string): string => {
  if (!standardCode || !standardCode.startsWith('880') || standardCode.length < 12) {
    return '';
  }
  return standardCode.substring(3, 12);
};

/**
 * 약품 DB 주성분 정리 (spec 2026-10-08 D6) — 정리 결과가 빈 값이면 ''
 * (a) trim (b) 정확히 '미상'이면 빈 값 (c) 끝의 ' / 주성분코드' 제거 (d) 연속 공백 → 공백 하나
 */
const cleanDrugDbIngredient = (value?: string | null): string => {
  const trimmed = (value || '').trim();
  if (trimmed === '미상') return '';
  return trimmed.replace(/\s*\/\s*[0-9]{6}[A-Z0-9]*$/, '').replace(/\s+/g, ' ');
};

/**
 * 엑셀 성분명 — drug.mainIngredient → drugMainIngredient(약품 DB, D6 정리) → ingredientName(심평원 원문, trim)
 * 성분 출처가 모두 없거나 '미상'이면 ''
 */
export const formatIngredientForExport = (p: Prescription): string => {
  const fromDrugDb = cleanDrugDbIngredient(p.drug?.mainIngredient) || cleanDrugDbIngredient(p.drugMainIngredient);
  if (fromDrugDb) return fromDrugDb;
  const original = (p.ingredientName || '').trim();
  return original === '미상' ? '' : original;
};

/**
 * 엑셀 약효분류 — 코드(drug.meftDivNo → meftDivNo)가 있으면 "코드 분류명"(처방 표 화면과 같은 표기),
 * 없으면 drugEfficacy(심평원 원문)
 */
export const formatMeftDivForExport = (p: Prescription): string => {
  const code = p.drug?.meftDivNo || p.meftDivNo;
  if (code) return `${code} ${meftDivName(code)}`.trim();
  return (p.drugEfficacy || '').trim();
};

/**
 * 환자 약력 엑셀 다운로드 (내가먹는약한눈에 형식)
 */
export function exportMedicationHistoryToExcel({
  patient,
  filename,
}: ExportMedicationHistoryParams): void {
  const workbook = XLSX.utils.book_new();

  // 처방전 데이터를 "내가먹는약한눈에" 형식 행으로 변환
  const prescriptionToRow = (p: Prescription, index: number, hospitalOverride?: string) => ({
    '번호': index + 1,
    '조제일자': p.prescriptionDate ? formatDateYYYYMMDD(p.prescriptionDate) : '',
    '처방기관': hospitalOverride || p.hospital || '',
    '조제기관': p.pharmacy || '',
    '제품명': p.medicationName || '',
    '약효분류': formatMeftDivForExport(p),             // 약효분류번호 + 분류명 (약제비 목록 분류)
    'ATC코드': p.drug?.atcCode || p.atcCode || '',     // ATC코드 (별도 분류 체계)
    '성분명': formatIngredientForExport(p),
    '약품코드': p.insuranceCode || extractInsuranceCodeFromStandardCode(p.standardCode),
    '단위': p.dosageUnit || '',
    '1회 투약량': p.dosagePerAdministration || '',
    '1일 투여횟수': p.timesPerDay || '',
    '총 투약일수': p.daysOfAdministration || '',
    '안전성 서한(속보)': '',                           // 데이터 없음
    '항혈전제 여부': '',                               // 데이터 없음
  });

  // 모든 약력 데이터 통합
  let index = 0;
  const allMedications = [
    ...(patient.prescriptions || []).map(p => prescriptionToRow(p, index++)),
    ...(patient.otc || []).map(p => prescriptionToRow(p, index++, '구매한일반의약품')),
    ...(patient.hff || []).map(p => prescriptionToRow(p, index++, '구매한건강기능식품')),
  ];

  // 워크시트 생성
  const worksheet = XLSX.utils.json_to_sheet(allMedications);

  // 열 너비 설정 (15컬럼)
  worksheet['!cols'] = [
    { wch: 6 },   // 번호
    { wch: 12 },  // 조제일자
    { wch: 20 },  // 처방기관
    { wch: 20 },  // 조제기관
    { wch: 40 },  // 제품명
    { wch: 25 },  // 약효분류
    { wch: 12 },  // ATC코드
    { wch: 30 },  // 성분명
    { wch: 12 },  // 약품코드
    { wch: 8 },   // 단위
    { wch: 10 },  // 1회 투약량
    { wch: 12 },  // 1일 투여횟수
    { wch: 10 },  // 총 투약일수
    { wch: 15 },  // 안전성 서한(속보)
    { wch: 12 },  // 항혈전제 여부
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, '약력');

  // 파일명 생성
  const today = new Date().toISOString().split('T')[0];
  const defaultFilename = `환자약력_${patient.id}_${today}.xlsx`;

  // 다운로드
  XLSX.writeFile(workbook, filename || defaultFilename);
}
